import { SignInRequired } from "../../auth/signIn";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Row, Select, Typography, theme } from "antd";
import { chartPalette, LoadingState, PageHeader, StatTile } from "@dowty/ui";
import { useAuth } from "../../context/AuthContext";
import type { DrinkActivitySummary } from "../../services/drinkService";
import { fetchDrinkActivities } from "../../services/drinks/DrinkService";
import { useTranslation } from "react-i18next";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { getRecapMetrics, getRecapYears } from "./drinkRecap/metrics";
import "./AnalyticsPages.css";

const { Text } = Typography;
const hiddenKey = (userId: number) => `saufdings-recap-hidden:${userId}`;

const YearInReview: React.FC = () => {
    const { isAuthenticated, user } = useAuth();
    const { t, i18n } = useTranslation();
    const { token } = theme.useToken();
    const [activities, setActivities] = useState<DrinkActivitySummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [hidden, setHidden] = useState(false);
    const requestId = useRef(0);

    const loadActivities = useCallback(async () => {
        if (!user) return;
        const currentRequest = ++requestId.current;
        setLoading(true);
        setLoadError(false);
        try {
            const result = await fetchDrinkActivities(user);
            if (currentRequest === requestId.current) setActivities(result);
        } catch {
            if (currentRequest === requestId.current) setLoadError(true);
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (isAuthenticated && user) {
            const isHidden = localStorage.getItem(hiddenKey(user.userId)) === "true";
            setHidden(isHidden);
            if (isHidden) {
                requestId.current += 1;
                setActivities([]);
                setLoadError(false);
                setLoading(false);
            } else {
                void loadActivities();
            }
        } else {
            requestId.current += 1;
            setActivities([]);
            setLoading(false);
        }
    }, [isAuthenticated, user, loadActivities]);

    const years = useMemo(() => getRecapYears(activities), [activities]);
    const metrics = useMemo(() => getRecapMetrics(activities, year), [activities, year]);
    const hasPreviousYear = years.includes(year - 1);
    const palette = chartPalette(typeof document !== "undefined" && document.documentElement.dataset.theme === "dark" ? "dark" : "light");
    const monthNames = useMemo(() => Array.from({ length: 12 }, (_, month) =>
        new Intl.DateTimeFormat(i18n.resolvedLanguage ?? i18n.language, { month: "short" }).format(new Date(2020, month, 1))),
    [i18n.language, i18n.resolvedLanguage]);
    const chartData = useMemo(() => metrics.monthlyCounts.map(({ month, count }) => ({ month: monthNames[month], count })), [metrics.monthlyCounts, monthNames]);
    const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language, { maximumFractionDigits: 1 }), [i18n.language, i18n.resolvedLanguage]);

    const toggleHidden = () => {
        if (!user) return;
        const next = !hidden;
        localStorage.setItem(hiddenKey(user.userId), String(next));
        setHidden(next);
        if (!next) void loadActivities();
    };

    if (!isAuthenticated || !user) {
        return <SignInRequired title={t("recap.authRequired.title")} description={t("recap.authRequired.description")} />;
    }

    if (loading) return <div className="analytics-page"><LoadingState variant="stats" label={t("recap.loading")} /></div>;

    let recapContent: React.ReactNode;
    if (loadError) {
        recapContent = <Alert type="error" showIcon message={t("recap.loadError")} action={<Button className="recap-retry" onClick={() => void loadActivities()}>{t("recap.retry")}</Button>} />;
    } else if (hidden) {
        recapContent = (
            <Card>
                <Text>{t("recap.hidden")}</Text>
                <Button className="recap-restore" onClick={toggleHidden}>{t("recap.show")}</Button>
            </Card>
        );
    } else if (metrics.totalDrinks === 0) {
        recapContent = (
            <Card>
                <Text>{t("recap.empty", { year })}</Text>
                <div><Button className="recap-hide" onClick={toggleHidden}>{t("recap.hide")}</Button></div>
            </Card>
        );
    } else {
        recapContent = (
            <>
                <Alert type="info" showIcon message={t("recap.summary", { year, drinks: metrics.totalDrinks, days: metrics.drinkingDays })} />
                <Text type="secondary">{t("recap.privacy")}</Text>
                <Row gutter={[12, 12]} className="drink-stats-summary">
                    <Col xs={12} md={6}><StatTile label={t("recap.metrics.totalDrinks")} value={metrics.totalDrinks} /></Col>
                    <Col xs={12} md={6}><StatTile label={t("recap.metrics.drinkingDays")} value={metrics.drinkingDays} /></Col>
                    <Col xs={12} md={6}><StatTile label={t("recap.metrics.average")} value={numberFormat.format(metrics.averagePerDrinkingDay)} /></Col>
                    <Col xs={12} md={6}><StatTile label={t("recap.metrics.busiestMonth")} value={`${monthNames[metrics.busiestMonth]} (${metrics.monthlyCounts[metrics.busiestMonth].count})`} /></Col>
                </Row>
                <Card title={t("recap.monthly.title")}>
                    <div className="analytics-chart recap-chart" role="img" aria-label={t("recap.monthly.chartLabel", { year })}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: -16 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                                <XAxis dataKey="month" tick={{ fill: token.colorTextSecondary, fontSize: 12 }} />
                                <YAxis allowDecimals={false} tick={{ fill: token.colorTextSecondary, fontSize: 12 }} />
                                <Tooltip contentStyle={{ backgroundColor: token.colorBgElevated, color: token.colorText }} />
                                <Bar dataKey="count" name={t("recap.monthly.drinks")} fill={palette[0]} isAnimationActive={typeof window !== "undefined" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <details className="recap-monthly-data">
                        <summary>{t("recap.monthly.tableLabel")}</summary>
                        <table>
                            <thead><tr><th scope="col">{t("recap.monthly.month")}</th><th scope="col">{t("recap.monthly.drinks")}</th></tr></thead>
                            <tbody>{chartData.map(({ month, count }) => <tr key={month}><td>{month}</td><td>{count}</td></tr>)}</tbody>
                        </table>
                    </details>
                </Card>
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}><Card title={t("recap.metrics.mostFrequentDrink")}><Text>{metrics.mostFrequentDrink}</Text></Card></Col>
                    <Col xs={24} md={12}>
                        <Card title={t("recap.comparison.title", { year: year - 1 })}>
                            <Text>{hasPreviousYear
                                ? t("recap.comparison.neutral", { difference: `${metrics.yearDifference > 0 ? "+" : ""}${metrics.yearDifference}` })
                                : t("recap.comparison.noData")}</Text>
                        </Card>
                    </Col>
                </Row>
                <Button className="recap-hide" onClick={toggleHidden}>{t("recap.hide")}</Button>
            </>
        );
    }

    return (
        <div className="analytics-page recap-page">
            <PageHeader
                title={t("recap.page.title")}
                subtitle={t("recap.page.subtitle")}
                extra={(
                    <div className="recap-year-picker">
                        <label htmlFor="recap-year">{t("recap.yearLabel")}</label>
                        <Select
                            id="recap-year"
                            aria-label={t("recap.yearLabel")}
                            value={year}
                            options={years.map((availableYear) => ({ value: availableYear, label: availableYear }))}
                            disabled={!years.length}
                            onChange={setYear}
                        />
                    </div>
                )}
            />
            {recapContent}
        </div>
    );
};

export default YearInReview;
